//#region react
import React, { useEffect, useRef, useState } from 'react';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { setShowEditTaskModal } from '_redux/slices/timeline/selected-task';
import { selectFirebaseUser, selectIata } from '_redux/slices/main-screen/user';
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { updateBooking } from '_redux/slices/prm-bookings/bookings';
import { resetUpdateTaskFlags, selectTimelineArrivalsBookingList, selectTimelineDeparturesBookingList, selectUpdateTaskStatus, updateTask } from '_redux/slices/timeline/timeline-data';
import { selectJob } from '_redux/slices/utils/menu-context';

// styles 
import { Colors, Spacing } from '_styles'

// tremor
import { Subtitle } from "@tremor/react";

// functions 
import { eqUnorderedStringArrays, formatDayTime, getAssistanceArray, getAssistanceString, getOriginalBooking, processName } from '_utils/functions';
//react-icons
import { TfiViewList } from "react-icons/tfi";
//bootstrap
import { Col, Row, Button, Form } from 'react-bootstrap';
//custom component
import { AssistanceSelector } from '_components/molecules';
import { CustomSpinner } from '_components/atoms';


//#endregion

const RowValue = ({ title, value }) => {
    return (
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", marginBottom: Spacing.SCALE_12, flex: 1 }}>
            <div style={{ flex: 1 }}>
                <Subtitle>{title}</Subtitle>
                <p>{value}</p>
            </div>
        </div>
    )
};


const GroupedPax = ({
    paxArray,
    selectedPaxId,
    onSelectPax,
    value,
    onChange,
    reasonsByPax,
    setReasonsByPax,
    originalMapRef,
    editAssistanceHistoric = [],
}) => {
    const usedTheme = useSelector(selectUsedTheme);

    return (
        <Row className="gap-2">
            {paxArray.map((p) => {
                const id = p.originalBookingId;
                const active = selectedPaxId === id;
                const original = originalMapRef.current?.[id] ?? [];
                const changed = !eqUnorderedStringArrays(value, original);
                const paxHistory = editAssistanceHistoric.filter((h) => h.bookingId === id);

                return (
                    <Col xs={12} key={id}>
                        <Button
                            onClick={() => onSelectPax(id)}
                            variant={active ? "outline-primary" : "outline-secondary"}
                            style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderRadius: 13,
                                fontSize: 12,
                                paddingBlock: 5,
                                cursor: "pointer",
                            }}
                        >
                            {processName(p.name) || ""}
                            <TfiViewList size={15} />
                        </Button>

                        {active && (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    rowGap: 12,
                                    columnGap: 48,
                                    marginTop: 16,
                                }}
                            >
                                {/* History */}
                                {paxHistory.length > 0 && (
                                    <div style={{ gridColumn: "1 / span 2" }}>
                                        <AssistanceHistory assistanceHistoric={paxHistory} />
                                    </div>
                                )}

                                {/* Selector */}
                                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE }}>Assistance</Form.Label>
                                <div style={{ gridColumn: "1 / span 2" }}>
                                    <AssistanceSelector
                                        value={value}
                                        defaultValue={value}
                                        setAssistance={(codes) => onChange(codes)}
                                    />
                                </div>

                                {/* Reason */}
                                {changed && (
                                    <Form.Control
                                        className="form-input"
                                        as="textarea"
                                        placeholder="Add Reason to edit assistance (Required)"
                                        onChange={(e) =>
                                            setReasonsByPax((prev) => ({ ...prev, [id]: e.target.value }))
                                        }
                                        value={reasonsByPax[id] || ""}
                                        style={{
                                            height: "60px",
                                            gridColumn: "1 / span 2",
                                            backgroundColor: usedTheme.BACKGROUND_COLOR,
                                            borderColor: usedTheme.BORDER_COLOR,
                                            color: usedTheme.TEXT_COLOR,
                                        }}
                                    />
                                )}
                            </div>
                        )}
                    </Col>
                );
            })}
        </Row>
    );
};


const AssistanceHistory = ({ assistanceHistoric }) => {
    const usedTheme = useSelector(selectUsedTheme);

    return (
        assistanceHistoric.length > 0 ? (
            <div className="mb-2" style={{ padding: "8px", border: `1px solid ${usedTheme.BORDER_COLOR}`, borderRadius: 8 }}>
                <Form.Label style={{ color: Colors.PRM_LIGHT_BLUE, marginBottom: 6 }}>
                    Assistance Edit History
                </Form.Label>

                {assistanceHistoric.map((h, i) => (
                    <div key={i} style={{ padding: "8px 0", borderTop: i ? `1px solid ${usedTheme.BORDER_COLOR}` : "none" }}>
                        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>
                            Edited by <strong>{h?.author.name || "Unknown"}</strong> At:{formatDayTime(h?.editedAt)}
                        </div>
                        <div style={{ fontSize: 13 }}>
                            <div><strong>Old:</strong> {getAssistanceString(h?.prev)}</div>
                            <div><strong>New:</strong> {getAssistanceString(h?.next)}</div>
                            <div style={{ marginTop: 4 }}>
                                <strong>Reason:</strong> {h?.reason || "—"}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        ) : <></>
    )
}

const buildOriginalMap = (job) => {
    if (Array.isFullArray(job?.passengers)) {
        const m = {};
        for (const p of job.passengers) {
            m[p.originalBookingId] = getAssistanceArray(p.assistance);
        }
        return m;
    }
    // single-pax task
    const singleId = job?.originalBookingId ?? job?._id?.split("_IATA")[0];
    return { [singleId]: getAssistanceArray(job?.passengerInformation?.assistance) };
};


const EditTask = () => {

    //#region redux
    const dispatch = useDispatch()
    const job = useSelector(selectJob);
    const iata = useSelector(selectIata)
    const firebaseUser = useSelector(selectFirebaseUser);
    const updateTaskStatus = useSelector(selectUpdateTaskStatus);
    const arrivalBookings = useSelector(selectTimelineArrivalsBookingList);                         // holds list of bookings returned from post request, used for patch request
    const departuresBookings = useSelector(selectTimelineDeparturesBookingList);                         // holds list of bookings returned from post request, used for patch request  
    const usedTheme = useSelector(selectUsedTheme);

    //#endregion

    //#region local states
    const [flightNumber, setFlightNumber] = useState("");
    const [departure, setDeparture] = useState("");
    const [arrival, setArrival] = useState("");

    const [paxEdits, setPaxEdits] = useState({});          // bookingId -> codes[]
    const [reasonsByPax, setReasonsByPax] = useState({});  // bookingId -> string
    const [selectedPaxId, setSelectedPaxId] = useState(null);
    const [singleAssistance, setSingleAssistance] = useState(["WCHR"]);
    const [singleReason, setSingleReason] = useState("");
    const [assistanceHistoric, setAssistanceHistoric] = useState([])
    const [showAddReasonInput, setShowAddReasonInput] = useState(false);
    const [flightType, setFlightType] = useState("D")   // check if it's arrival or departures
    const [error, setError] = useState("");
    const originalMapRef = useRef({});


    const author = {
        id: firebaseUser?.uid || firebaseUser?.id || "unknown",
        name: firebaseUser?.displayName || firebaseUser?.name || firebaseUser?.email || "Unknown",
    };
    //#endregion

    //#region useEffect
    // Set flight and passenger info
    useEffect(() => {
        setFlightNumber(job?.flightInformation?.flightNumber);
        setDeparture(job?.flightInformation?.departureAirport);
        setArrival(job?.flightInformation?.arrivalAirport);

        // snapshot originals for change detection
        const orig = buildOriginalMap(job);
        originalMapRef.current = orig;

        if (Array.isFullArray(job?.passengers)) {
            const prefill = {};
            for (const p of job.passengers) prefill[p.originalBookingId] = getAssistanceArray(p.assistance);
            setPaxEdits(prefill);
            setSelectedPaxId(job.passengers[0]?.originalBookingId ?? null);
            setReasonsByPax({});
            setSingleReason("");
        } else {
            const singleId = job?.originalBookingId ?? job?._id?.split("_IATA")?.[0];
            setSelectedPaxId(singleId); // treat single as one pax id
            const singleOrig = getAssistanceArray(job?.passengerInformation?.assistance);
            setSingleAssistance(singleOrig);
            setPaxEdits({ [singleId]: singleOrig });
            setReasonsByPax({});
            setSingleReason("");
        }
        setFlightType(job.flightInformation.arrivalAirport === iata ? "A" : "D")
        setAssistanceHistoric(job.editAssistanceHistoric ?? [])
    }, [job]);

    useEffect(() => {
        if (updateTaskStatus === "succeeded") {
            dispatch(resetUpdateTaskFlags())
            dispatch(setShowEditTaskModal(false))

        }
    }, [updateTaskStatus])

    const handleSelectPax = (id) => {
        if (!(id in paxEdits)) {
            const pax = job.passengers.find(p => p.originalBookingId === id);
            setPaxEdits(prev => ({ ...prev, [id]: getAssistanceArray(pax?.assistance) }));
        }
        setSelectedPaxId(id);
    };

    const currentAssistance =
        selectedPaxId ? (paxEdits[selectedPaxId] || []) : singleAssistance;



    const handleAssistanceChange = (codes) => {
        if (originalMapRef.current && Object.keys(originalMapRef.current).length === 0) return;
        const next = getAssistanceArray(codes);

        // multi-pax
        if (Array.isFullArray(job?.passengers)) {
            const id = selectedPaxId;
            const prev = originalMapRef.current[id] ?? [];
            const changed = !eqUnorderedStringArrays(next, prev);

            setPaxEdits((prev) => ({
                ...prev,
                [id]: next,
            }));

            if (!changed) {
                setReasonsByPax((prev) => ({ ...prev, [id]: "" }));
            }
            return;
        }

        // single-pax
        const id = job?.originalBookingId ?? job?._id?.split("_IATA")?.[0];
        const prev = originalMapRef.current[id] ?? [];
        const changed = !eqUnorderedStringArrays(next, prev);
        setSingleAssistance(next);
        setShowAddReasonInput(changed);
        if (!changed) setSingleReason("");
    };


    const makeHistoryEntry = (prevArr, nextArr, reason, bookingId) => ({
        bookingId,                // helpful for multi-pax tasks
        prev: prevArr,
        next: nextArr,
        reason: reason.trim(),
        author,
        editedAt: new Date().toISOString(),
    });

    // changedEntries: [{ bookingId, prev, next, reason, author, editedAt }]
    const prepareBookingsForUpdating = (changedEntries) => {
        const bookingsList = flightType === "A" ? arrivalBookings : departuresBookings; // map: id -> bookingDoc
        const bookings = bookingsList.map(b => b.doc)
        return changedEntries.map((entry) => {
            const original = getOriginalBooking(bookings, entry.bookingId) || {}; // fall back to empty if not found
            const oldHist = Array.isArray(original.editAssistanceHistoric)
                ? original.editAssistanceHistoric
                : [];

            return {
                id: entry.bookingId,
                assistance: entry.next,
                editAssistanceHistoric: [...oldHist, entry],
            };
        });
    };

    const handleSave = () => {
        setError("");

        const passengers = Array.isFullArray(job?.passengers) ? job.passengers : null;
        const changedEntries = [];
        let continueProcess = true;
        if (passengers) {
            // multi-pax
            const updatedPassengers = job.passengers.map(p => {
                const id = p.originalBookingId;
                const prevArr = originalMapRef.current[id] ?? getAssistanceArray(p.assistance);
                const nextArr = paxEdits[id] ?? prevArr;
                const changed = !eqUnorderedStringArrays(prevArr, nextArr);

                // validate reason for changed pax
                if (changed) {
                    const reason = (reasonsByPax[id] || "").trim();
                    if (!reason) {
                        setError(`Please add a reason for editing assistance for ${processName(p.name) || id}.`);
                        continueProcess = false
                    }
                    changedEntries.push(makeHistoryEntry(prevArr, nextArr, reason, id));
                }
                return { ...p, assistance: nextArr };
            });

            // Append task-level history (keep existing + new)
            const newTaskHistory = [
                ...(Array.isArray(job.editAssistanceHistoric) ? job.editAssistanceHistoric : []),
                ...changedEntries,
            ];
            if (!continueProcess) return

            // 1) Update task with passengers + history
            dispatch(updateTask({
                id: job._id,
                passengers: updatedPassengers,
                editAssistanceHistoric: newTaskHistory,
            }));

            const bookingUpdates = prepareBookingsForUpdating(changedEntries);
            bookingUpdates.forEach((b) => {
                const payload = {
                    id: b._id || b.id,
                    body: {
                        assistance: b.assistance,
                        editAssistanceHistoric: b.editAssistanceHistoric
                    }
                }

                dispatch(updateBooking(payload))
            }
            );

        } else {
            // single-pax task
            const bookingId = job.originalBookingId ?? job._id.split("_IATA")[0]
            const prevArr = getAssistanceArray(job?.passengerInformation?.assistance);
            const nextArr = getAssistanceArray(singleAssistance);
            const changed = !eqUnorderedStringArrays(prevArr, nextArr);

            // if changed, require reason
            if (changed && !singleReason.trim()) {
                setError("Please add a reason for editing the assistance.");
                return;
            }

            const passengerInformation = {
                ...job.passengerInformation,
                assistance: nextArr,
            };

            const newTaskHistory = [
                ...(Array.isArray(job?.editAssistanceHistoric) ? job.editAssistanceHistoric : []),
                ...(changed ? [makeHistoryEntry(prevArr, nextArr, singleReason, bookingId)] : []),
            ];

            // 1) Update task
            dispatch(updateTask({
                id: job._id,
                passengerInformation,
                editAssistanceHistoric: newTaskHistory,
            }));
            // 2) Patch its booking
            const bookingsList = flightType === "A" ? arrivalBookings : departuresBookings;
            const bookings = bookingsList.map(b => b.doc)
            const originalBookingDoc = getOriginalBooking(bookings, bookingId)
            const oldHist = originalBookingDoc["editAssistanceHistoric"] ?? [];

            const historyEntry = makeHistoryEntry(prevArr, nextArr, singleReason, bookingId);
            const bookingForUpdate = {
                id: bookingId,
                body: {
                    assistance: nextArr,
                    editAssistanceHistoric: [...oldHist, historyEntry],

                }
            };

            if (changed) {
                dispatch(updateBooking(bookingForUpdate));
            }
        }
    };
    //#endregion


    return (
        <div style={{ marginBottom: Spacing.SCALE_16 }}>

            {/* Flight Information */}
            <div style={{ marginBottom: Spacing.SCALE_24 }}>
                <Subtitle>Flight Information</Subtitle>
                <hr />
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        rowGap: Spacing.SCALE_12,
                        columnGap: Spacing.SCALE_48,
                        marginTop: Spacing.SCALE_16,
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <RowValue title="Flight Number" value={flightNumber} />
                    <RowValue title="Departure" value={departure} />
                    <RowValue title="Arrival" value={arrival} />

                </div>
            </div>

            {/* Passenger Information */}
            <div style={{ marginBottom: Spacing.SCALE_24 }}>
                <Subtitle>Passenger Information</Subtitle>
                <hr />
                {Array.isFullArray(job.passengers) ?
                    <GroupedPax
                        paxArray={job.passengers}
                        selectedPaxId={selectedPaxId}
                        onSelectPax={handleSelectPax}
                        value={currentAssistance}
                        onChange={handleAssistanceChange}
                        reasonsByPax={reasonsByPax}
                        setReasonsByPax={setReasonsByPax}
                        originalMapRef={originalMapRef}
                        editAssistanceHistoric={assistanceHistoric}
                    />
                    : (
                        <>
                            <AssistanceHistory assistanceHistoric={assistanceHistoric} />
                            <div >
                                <Form.Label style={{ color: Colors.GRAY_DARK }}>Assistance</Form.Label>
                                <AssistanceSelector
                                    value={singleAssistance}
                                    defaultValue={singleAssistance}
                                    setAssistance={handleAssistanceChange}
                                />
                                {showAddReasonInput && (
                                    <Form.Control
                                        className="form-input"
                                        as="textarea"
                                        type="text"
                                        placeholder="Add Reason to edit assistance (Required)"
                                        onChange={(e) => setSingleReason(e.target.value)}
                                        value={singleReason}
                                        style={{ height: '60px', marginTop: "4px", backgroundColor: usedTheme.BACKGROUND_COLOR, borderColor: usedTheme.BORDER_COLOR, color: usedTheme.TEXT_COLOR }}

                                    />
                                )}

                            </div>
                        </>
                    )}
            </div>

            {/* Save Button */}
            <div className="mt-4" style={{ display: "flex", justifyContent: "center" }}>
                <Button
                    variant="outline-primary"
                    onClick={handleSave}
                    style={{ width: 200 }}
                >
                    {updateTaskStatus == 'loading' ? <CustomSpinner variant="light" size={"small"} /> : 'Save Task'}
                </Button>
            </div>

        </div>
    );
};

export default EditTask;